import type { TFunction } from "i18next";
import { ERROR_MESSAGES, DEFAULT_ERROR, NETWORK_ERROR, type ErrorCopy } from "./messages.js";

/** A named entity the dialog can list (e.g. a facility holding a category). */
export interface ErrorEntity {
  id: number;
  name: string;
}
/** Optional structured detail, rendered as an expandable list in the dialog. */
export interface ErrorDetails {
  label: string;
  items: ErrorEntity[];
}
/** The dialog's already-TRANSLATED copy. Resolution happens at render, so a language switch re-renders it. */
export interface ResolvedError {
  title: string;
  message: string;
  details?: ErrorDetails;
}

/** The shape a thrown mutation error carries (see the `fail()` helpers): status + code + the raw response body. */
interface ThrownError {
  status?: number;
  code?: string;
  body?: unknown;
}

/**
 * Turn any thrown error into `{ title, message, details? }` for the shared ErrorDialog. Reads `code` for the copy and
 * `body` for structured detail (e.g. CATEGORY_HAS_ATTACHMENTS → the attached facilities). A mapped code wins; a
 * response with no known code but a status is a generic server error; no status at all reads as a network failure.
 */
export function resolveError(err: unknown, t: TFunction): ResolvedError {
  const e = (err ?? {}) as ThrownError;
  const mapped = e.code ? (ERROR_MESSAGES as Record<string, ErrorCopy>)[e.code] : undefined;
  const copy: ErrorCopy = mapped ?? (e.status ? DEFAULT_ERROR : NETWORK_ERROR);
  return {
    title: t(copy.titleKey),
    message: messageOverride(e, t) ?? t(copy.messageKey),
    details: detailsFor(e, t),
  };
}

/**
 * A few codes carry a SHAPE that changes what the operator should do. ADR-035: a sensor blocked only by a BLUEPRINT
 * element belongs to no manual, so the default "remove it from the manuals below" copy would point at an EMPTY list
 * (diff panel) — say what actually blocks it instead.
 */
function messageOverride(e: ThrownError, t: TFunction): string | undefined {
  if (e.code === "SENSOR_IN_USE") {
    const body = e.body as { manuals?: unknown[]; elementSettings?: number } | undefined;
    const elements = body?.elementSettings ?? 0;
    if ((body?.manuals?.length ?? 0) === 0 && elements > 0) {
      return t("errors.sensorBoundToElements", { count: elements });
    }
  }
  return undefined;
}

function detailsFor(e: ThrownError, t: TFunction): ErrorDetails | undefined {
  if (e.code === "CATEGORY_HAS_ATTACHMENTS") {
    const facilities = (e.body as { facilities?: ErrorEntity[] } | undefined)?.facilities;
    if (facilities && facilities.length > 0) {
      return { label: t("errors.attachedToFacilities", { count: facilities.length }), items: facilities };
    }
  }
  // ADR-035: a refused sensor delete names the manuals still binding it, with the trashed/archived ones marked — the
  // operator otherwise has to hunt for a manual that isn't even in the active list.
  if (e.code === "SENSOR_IN_USE") {
    const manuals = (e.body as { manuals?: Array<ErrorEntity & { state?: string }> } | undefined)?.manuals;
    if (manuals && manuals.length > 0) {
      return {
        label: t("errors.usedByManuals", { count: manuals.length }),
        items: manuals.map((m) => ({ id: m.id, name: m.state && m.state !== "live" ? `${m.name} (${m.state})` : m.name })),
      };
    }
  }
  return undefined;
}
